import React, { useEffect, useState } from "react";
import AdminLayout from "../../components/admin/AdminLayout";
import api from "../../utils/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import {
  Music, RefreshCw, ExternalLink, CheckCircle2, XCircle,
  Clock, List, Settings2, Loader2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SpotifyPlaylistPage() {
  const { token } = useAuth();
  const [status, setStatus]   = useState(undefined);
  const [preview, setPreview] = useState(null);
  const [syncPreview, setSyncPreview] = useState(null);   // Dry-Run Ergebnis
  const [settings, setSettings] = useState({ months: 3 });
  const [syncing, setSyncing]         = useState(false);
  const [loadingPreview, setLoadingPreview]     = useState(false);
  const [loadingSyncPreview, setLoadingSyncPreview] = useState(false);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const auth = params.get("auth");
    if (auth === "success") toast.success("Spotify erfolgreich verbunden!");
    if (auth === "error")   toast.error("Spotify-Verbindung fehlgeschlagen.");
    if (auth) window.history.replaceState({}, "", window.location.pathname);
  }, []);

  useEffect(() => {
    if (!token) return;
    loadStatus();
  }, [token]);

  const handleConnect = async () => {
    try {
      const { data } = await api.get("/spotify/playlist/auth-url");
      window.location.href = data.url;
    } catch { toast.error("Spotify-URL konnte nicht geladen werden."); }
  };

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/spotify/playlist/status");
      setStatus(data);
      setSettings({ months: data.months || 3 });
    } catch {
      setStatus(null);
      toast.error("Status konnte nicht geladen werden.");
    }
  };

  const loadPreview = async () => {
    setLoadingPreview(true);
    try {
      const { data } = await api.get(`/spotify/playlist/preview-releases?months=${settings.months}`);
      setPreview(data);
    } catch { toast.error("Vorschau fehlgeschlagen."); }
    finally { setLoadingPreview(false); }
  };

  const loadSyncPreview = async () => {
    setLoadingSyncPreview(true);
    setSyncPreview(null);
    try {
      const { data } = await api.get("/spotify/playlist/preview-sync");
      setSyncPreview(data.results?.[0] || null);
    } catch { toast.error("Vorschau fehlgeschlagen."); }
    finally { setLoadingSyncPreview(false); }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/spotify/playlist/sync");
      const r = data.results?.[0];
      if (r?.skipped) {
        toast.info(r.reason || "Nichts zu synchronisieren.");
      } else {
        toast.success(`Sync abgeschlossen! ${r?.tracks_added ?? 0} Tracks in Playlist.`);
      }
      setSyncPreview(null);
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync fehlgeschlagen.");
    } finally { setSyncing(false); }
  };

  const handleSaveSettings = async () => {
    try {
      await api.put("/spotify/playlist/settings", { months: settings.months });
      toast.success("Einstellungen gespeichert.");
      loadStatus();
    } catch { toast.error("Speichern fehlgeschlagen."); }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Spotify-Verbindung trennen? Du musst danach neu autorisieren.")) return;
    try {
      await api.post("/spotify/playlist/disconnect");
      toast.success("Getrennt. Bitte neu verbinden.");
      loadStatus();
    } catch { toast.error("Trennen fehlgeschlagen."); }
  };

  const inp = "w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/50";

  return (
    <AdminLayout>
      <div className="p-8 space-y-6">

        {/* Page header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-black text-white font-heading flex items-center gap-2">
              <Music size={22} className="text-emerald-400" /> Spotify Playlist
            </h1>
            <p className="text-zinc-400 text-sm mt-1">
              Automatische Verwaltung der HUWND-Playlist(s) · letzten 3 Monate
            </p>
          </div>
          {status?.authorized && (
            <div className="flex items-center gap-2">
              <button onClick={loadSyncPreview} disabled={loadingSyncPreview || syncing}
                className="flex items-center gap-2 px-4 py-2.5 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg border border-zinc-700 transition-colors">
                <RefreshCw size={14} className={loadingSyncPreview ? "animate-spin" : ""} />
                {loadingSyncPreview ? "Analysiere…" : "Sync-Vorschau"}
              </button>
              <button onClick={handleDisconnect}
                className="flex items-center gap-2 px-3 py-2.5 bg-zinc-900 hover:bg-rose-950 text-zinc-500 hover:text-rose-400 text-sm rounded-lg border border-zinc-800 hover:border-rose-500/30 transition-colors">
                Trennen
              </button>
            </div>
          )}
        </div>

        {/* Sync-Vorschau Panel */}
        {syncPreview && !syncPreview.error && (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-950/20 p-4 space-y-3">
            <p className="text-sm font-semibold text-emerald-400 flex items-center gap-2">
              <RefreshCw size={14} /> Sync-Vorschau
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: "Aktuell in Playlist", value: syncPreview.current_tracks, color: "text-zinc-300" },
                { label: "Nach Sync", value: syncPreview.new_tracks, color: "text-white" },
                { label: "Werden hinzugefügt", value: syncPreview.to_add, color: "text-emerald-400" },
                { label: "Werden entfernt", value: syncPreview.to_remove, color: "text-rose-400" },
              ].map(({ label, value, color }) => (
                <div key={label} className="rounded-lg bg-zinc-900/60 border border-zinc-800 px-3 py-2.5 text-center">
                  <p className={`text-2xl font-black ${color}`}>{value}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">{label}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-zinc-500">
              Aus <span className="text-white">{syncPreview.releases_found}</span> Releases (letzte 3 Monate) werden Track-URIs ermittelt.
              <span className="text-zinc-600"> · {syncPreview.unchanged} Tracks bleiben unverändert.</span>
            </p>
            <div className="flex items-center gap-3 pt-1">
              <button onClick={handleSync} disabled={syncing}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors">
                <RefreshCw size={13} className={syncing ? "animate-spin" : ""} />
                {syncing ? "Synchronisiere…" : "Jetzt synchronisieren"}
              </button>
              <button onClick={() => setSyncPreview(null)}
                className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors">
                Abbrechen
              </button>
            </div>
          </div>
        )}

        {/* Sync-Vorschau Fehler */}
        {syncPreview?.error && (
          <div className="rounded-xl border border-rose-500/30 bg-rose-950/20 p-4 text-sm text-rose-400">
            Fehler bei der Vorschau: {syncPreview.error}
          </div>
        )}

        {/* Loading */}
        {status === undefined && (
          <div className="flex items-center gap-2 text-zinc-500 text-sm">
            <Loader2 size={15} className="animate-spin" /> Lade Status…
          </div>
        )}

        {/* Auth card */}
        {status !== undefined && (
          <div className="bg-zinc-900 border border-white/5 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-white/5">
              <h2 className="text-sm font-semibold text-white">Spotify-Verbindung</h2>
            </div>
            <div className="p-5">
              {status?.authorized ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 size={18} className="text-emerald-400 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-white">Spotify verbunden</p>
                      {status.spotify_user ? (
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-zinc-300 font-medium">
                            {status.spotify_user.display_name || status.spotify_user.id}
                          </span>
                          {status.spotify_user.email && (
                            <span className="text-xs text-zinc-500">({status.spotify_user.email})</span>
                          )}
                          {status.spotify_user.product === "premium" && (
                            <span className="text-[10px] bg-emerald-900/60 text-emerald-400 border border-emerald-500/30 px-1.5 py-0.5 rounded font-bold">PREMIUM</span>
                          )}
                        </div>
                      ) : (
                        status.authorized_at && (
                          <p className="text-xs text-zinc-500">Autorisiert am {new Date(status.authorized_at).toLocaleDateString("de-DE")}</p>
                        )
                      )}
                    </div>
                    <button onClick={handleConnect}
                      className="text-xs text-zinc-500 hover:text-white border border-zinc-700 hover:border-zinc-600 px-3 py-1.5 rounded-lg transition-colors">
                      Neu verbinden
                    </button>
                  </div>
                  {/* Hinweis: Playlist-Zugriff */}
                  <div className="rounded-lg bg-zinc-800 border border-zinc-700 px-3 py-2.5 text-xs text-zinc-300 space-y-1.5">
                    <p><span className="font-semibold text-white">Wichtig:</span> Das verbundene Konto muss Eigentümer oder Mitarbeiter der Playlist sein.</p>
                    {/* Scopes */}
                    {status.granted_scope && (
                      <div>
                        <span className="text-zinc-500">Gewährte Scopes: </span>
                        {status.granted_scope.split(" ").map(s => (
                          <span key={s} className={`inline-block mr-1 px-1.5 py-0.5 rounded text-[10px] font-mono ${
                            s.includes("playlist") ? "bg-emerald-900/60 text-emerald-300" : "bg-zinc-700 text-zinc-400"
                          }`}>{s}</span>
                        ))}
                      </div>
                    )}
                    {/* Playlist-Zugriff Test */}
                    {status.playlist_access === false && (
                      <p className="text-amber-300 font-semibold">
                        Kein Zugriff auf eigene Playlists → Spotify Developer App: Konto als Test-User eintragen (siehe unten)
                      </p>
                    )}
                    {status.playlist_access === true && (
                      <p className="text-emerald-400">Playlist-Zugriff OK</p>
                    )}
                  </div>
                  {/* Anleitung wenn playlist_access false */}
                  {status.playlist_access === false && (
                    <div className="rounded-lg bg-zinc-900 border border-amber-500/40 px-4 py-3 space-y-2 text-xs text-zinc-300">
                      <p className="font-semibold text-amber-300">Lösung: Test-User in Spotify Developer Dashboard eintragen</p>
                      <ol className="list-decimal list-inside space-y-1 text-zinc-400">
                        <li>Öffne <a href="https://developer.spotify.com/dashboard" target="_blank" rel="noreferrer" className="text-emerald-400 underline">developer.spotify.com/dashboard</a></li>
                        <li>Wähle deine App (Client ID: <code className="bg-zinc-800 px-1 rounded">{String(process.env.REACT_APP_BACKEND_URL || "").includes("huwnd") ? "52601499..." : "deine App"}</code>)</li>
                        <li>Gehe zu <strong className="text-white">Settings → User Management</strong></li>
                        <li>Füge die E-Mail <strong className="text-white">{status.spotify_user?.email || "deines Spotify-Kontos"}</strong> hinzu</li>
                        <li>Danach: Trennen & Neu verbinden</li>
                      </ol>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Prominenter Connect-Button – zuerst */}
                  <button
                    onClick={handleConnect}
                    className="w-full flex items-center justify-center gap-2 px-5 py-3 text-black font-bold text-sm rounded-lg hover:opacity-90 transition-opacity"
                    style={{ background: "#1DB954" }}
                  >
                    <Music size={16} /> Mit Spotify verbinden
                  </button>

                  <div className="flex items-start gap-3">
                    <XCircle size={18} className="text-zinc-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-white">Nicht verbunden</p>
                      <p className="text-xs text-zinc-500 mt-0.5">
                        Einmalige Autorisierung erforderlich, damit die App Playlists in deinem Namen bearbeiten kann.
                      </p>
                    </div>
                  </div>

                  <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4 space-y-2">
                    <p className="text-xs font-semibold text-zinc-300">So funktioniert es</p>
                    <ol className="space-y-1.5 text-xs text-zinc-500">
                      <li><span className="text-emerald-500 font-bold mr-1">1.</span>Klicke oben auf "Mit Spotify verbinden"</li>
                      <li><span className="text-emerald-500 font-bold mr-1">2.</span>Melde dich mit dem HUWND-Spotify-Account an</li>
                      <li><span className="text-emerald-500 font-bold mr-1">3.</span>Bestätige die Berechtigung "Playlists bearbeiten"</li>
                      <li><span className="text-emerald-500 font-bold mr-1">4.</span>Du wirst automatisch hierher zurückgeleitet</li>
                    </ol>
                    {status?.redirect_uri && (
                      <p className="text-[11px] text-zinc-600 pt-1 break-all">
                        Redirect URI: <code className="font-mono">{status.redirect_uri}</code>
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        {status?.authorized && (
          <>
            <div className="flex gap-1 border-b border-zinc-800">
              {[
                { id: "overview", label: "Playlists", icon: Music },
                { id: "preview",  label: "Vorschau",  icon: List },
                { id: "settings", label: "Einstellungen", icon: Settings2 },
              ].map(({ id, label, icon: Icon }) => (
                <button key={id} onClick={() => setTab(id)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                    tab === id
                      ? "border-emerald-500 text-white"
                      : "border-transparent text-zinc-500 hover:text-zinc-300"
                  }`}>
                  <Icon size={13} /> {label}
                </button>
              ))}
            </div>

            {/* Tab: Playlists */}
            {tab === "overview" && (
              <div className="space-y-3">
                {status.playlists?.map(pl => (
                  <div key={pl.id} className="bg-zinc-900 border border-white/5 rounded-xl p-5">
                    {pl.error ? (
                      <p className="text-red-400 text-sm">Fehler: {pl.error}</p>
                    ) : (
                      <div className="flex gap-4">
                        {pl.cover_url && (
                          <img src={pl.cover_url} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-semibold text-white text-sm">{pl.name}</p>
                            {pl.url && (
                              <a href={pl.url} target="_blank" rel="noopener noreferrer"
                                className="text-zinc-500 hover:text-emerald-400 transition-colors flex-shrink-0">
                                <ExternalLink size={14} />
                              </a>
                            )}
                          </div>
                          {pl.description && <p className="text-xs text-zinc-500 mt-0.5 truncate">{pl.description}</p>}
                          <div className="flex flex-wrap gap-4 mt-2">
                            <span className="text-xs text-zinc-400"><span className="text-white font-medium">{pl.total_tracks}</span> Tracks aktuell</span>
                            {pl.last_tracks_count > 0 && <span className="text-xs text-zinc-400">Letzter Sync: <span className="text-white font-medium">{pl.last_tracks_count}</span> Tracks</span>}
                            {pl.last_sync && (
                              <span className="text-xs text-zinc-500 flex items-center gap-1">
                                <Clock size={10} />
                                {new Date(pl.last_sync).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                <p className="text-xs text-zinc-600">Automatische Synchronisation: jeden Montag um 00:05 Uhr</p>
              </div>
            )}

            {/* Tab: Vorschau */}
            {tab === "preview" && (
              <div className="bg-zinc-900 border border-white/5 rounded-xl overflow-hidden">
                <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-white">Release-Vorschau</h2>
                  <div className="flex items-center gap-2">
                    <select value={settings.months} onChange={e => setSettings(s => ({ ...s, months: +e.target.value }))}
                      className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1 text-xs text-white">
                      {[1,2,3,4,5,6].map(m => <option key={m} value={m}>{m} Monat{m > 1 ? "e" : ""}</option>)}
                    </select>
                    <button onClick={loadPreview} disabled={loadingPreview}
                      className="flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-white text-xs px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50">
                      <RefreshCw size={11} className={loadingPreview ? "animate-spin" : ""} /> Laden
                    </button>
                  </div>
                </div>
                <div className="p-5">
                  {!preview ? (
                    <p className="text-xs text-zinc-600">Klicke "Laden" um die Releases der gewählten Zeitspanne zu sehen.</p>
                  ) : (
                    <>
                      <p className="text-xs text-zinc-400 mb-3"><span className="text-white font-medium">{preview.count}</span> Releases mit Spotify-Link</p>
                      <div className="space-y-1 max-h-80 overflow-y-auto">
                        {preview.releases.map((r, i) => (
                          <div key={r.id} className="flex items-center gap-3 py-1.5">
                            <span className="text-xs text-zinc-600 w-5 text-right">{i + 1}</span>
                            {r.cover_url && <img src={r.cover_url} alt="" className="w-7 h-7 rounded object-cover flex-shrink-0" />}
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-white truncate">{r.interpret} – {r.titel}</p>
                              <p className="text-[10px] text-zinc-500">{r.erscheinungsdatum}</p>
                            </div>
                            {r.spotify && (
                              <a href={r.spotify} target="_blank" rel="noopener noreferrer" className="text-zinc-600 hover:text-emerald-400">
                                <ExternalLink size={11} />
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Tab: Einstellungen */}
            {tab === "settings" && (
              <div className="bg-zinc-900 border border-white/5 rounded-xl overflow-hidden">
                <div className="px-5 py-4 border-b border-white/5">
                  <h2 className="text-sm font-semibold text-white">Playlist-Einstellungen</h2>
                </div>
                <div className="p-5 space-y-5">
                  <div>
                    <label className="text-sm text-zinc-300 font-medium block mb-2">Zeitraum in der Playlist</label>
                    <select value={settings.months} onChange={e => setSettings(s => ({ ...s, months: +e.target.value }))}
                      className={`w-48 ${inp}`}>
                      {[1,2,3,4,5,6].map(m => <option key={m} value={m}>{m} Monat{m > 1 ? "e" : ""}</option>)}
                    </select>
                    <p className="text-xs text-zinc-500 mt-1.5">Releases älter als {settings.months} Monate werden automatisch entfernt.</p>
                  </div>
                  <button onClick={handleSaveSettings}
                    className="flex items-center gap-2 px-5 py-2.5 bg-rose-600 hover:bg-rose-500 text-white text-sm font-semibold rounded-lg transition-colors">
                    Einstellungen speichern
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
